import { Link } from "@/lib/router-compat";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, FileText, UserCheck, Upload, AlertTriangle, Ban } from "lucide-react";


const TermsOfServicePage = () => {
  const { t } = useTranslation();

  useSEO({
    title: t("termsPage.seoTitle", "Terms of Service | System Atlas"),
    description: t("termsPage.seoDescription", "System Atlas terms of service: usage rules, responsibilities, content ownership, and limitations."),
  });

  return (
    <>
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" />
          {t("termsPage.backToHome", "Back to home")}
        </Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">
            {t("termsPage.title", "Terms of Service")}
          </h1>

          <p className="text-sm text-muted-foreground mb-10">
            {t("termsPage.lastUpdated", "Last updated: February 16, 2026")}
          </p>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {t("termsPage.acceptanceTitle", "Acceptance of Terms")}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {t("termsPage.acceptanceContent", "By accessing and using System Atlas, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.")}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" />
              {t("termsPage.servicesTitle", "Description of Services")}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t("termsPage.servicesIntro", "System Atlas is a learning platform that allows you to:")}
            </p>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2 ml-2">
              <li>{t("termsPage.service1", "Transform course documents into structured revision sheets using Atlas")}</li>
              <li>{t("termsPage.service2", "Ask questions about the content of your courses")}</li>
              <li>{t("termsPage.service3", "Test your understanding with automatically generated quizzes")}</li>
              <li>{t("termsPage.service4", "Organize your subjects and track your progress")}</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              {t("termsPage.contentTitle", "User Content")}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t("termsPage.contentParagraph1", "You retain ownership of all content you upload to System Atlas. By uploading content, you grant us a limited license to process it as part of our services (sheet generation, quizzes, Atlas answers).")}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              {t("termsPage.contentParagraph2", "You are responsible for the content you upload and warrant that you have the necessary rights to share it. Uploading illegal, defamatory, or infringing content is prohibited.")}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              {t("termsPage.liabilityTitle", "Limitation of Liability")}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              {t("termsPage.liabilityParagraph1", "Content generated by artificial intelligence is provided for informational and educational purposes only. It does not substitute professional instruction and may contain errors.")}
            </p>
            <p className="text-muted-foreground leading-relaxed">
              {t("termsPage.liabilityParagraph2", "System Atlas is provided \"as is\" without warranty of any kind. We are not liable for any indirect, incidental, or consequential damages resulting from the use of our services.")}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Ban className="w-5 h-5 text-primary" />
              {t("termsPage.prohibitedTitle", "Prohibited Conduct")}
            </h2>
            <ul className="list-disc list-inside text-muted-foreground leading-relaxed space-y-2 ml-2">
              <li>{t("termsPage.prohibited1", "Using the service for illegal or unauthorized purposes")}</li>
              <li>{t("termsPage.prohibited2", "Attempting to bypass usage limits or security measures")}</li>
              <li>{t("termsPage.prohibited3", "Uploading malicious or harmful content")}</li>
              <li>{t("termsPage.prohibited4", "Reselling or redistributing Atlas-generated content without permission")}</li>
              <li>{t("termsPage.prohibited5", "Using bots or automated scripts to access the service")}</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{t("termsPage.modificationsTitle", "Modifications")}</h2>
            <p className="text-muted-foreground leading-relaxed">
              {t("termsPage.modificationsContent", "We reserve the right to modify these terms at any time. Users will be notified of significant changes. Continued use of the service after modifications constitutes acceptance of the new terms.")}
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold mb-4">{t("termsPage.linksTitle", "Links to other pages")}</h2>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/legal" className="text-primary hover:underline">
                {t("termsPage.legalLink", "Legal Notice")}
              </Link>
              <Link to="/privacy" className="text-primary hover:underline">
                {t("termsPage.privacyLink", "Privacy Policy")}
              </Link>
            </div>
          </section>
        </article>
      </div>
    </>
  );
};

export default TermsOfServicePage;
